<template>
  <div id="app" class="font-epilogue h-screen overflow-visible">
    <VerifyEmailNotification v-if="!isEmailConfirmed" />
    <StartupNotifications v-if="showStartupNotifications" />

    <div id="app-container" :class="appClass">
      <MobileSidebar v-if="$breakpoints.mdAndDown" />
      <Sidebar v-else />

      <div id="app-content-wrapper" :class="contentClass">
        <Navbar
          v-if="$breakpoints.mdAndDown"
          :class="{ 'mt-12': !isEmailConfirmed }"
        />

        <h1 v-if="$breakpoints.mdAndUp && !customHeader">
          {{ activeViewName }}
        </h1>

        <NoPackages v-if="noPackages && !canShowWithNoPackage" />
        <NoProduct
          v-else-if="!hasProduct && !canShowWithNoProduct"
          class="flex-1"
        />
        <router-view v-else class="flex-1" />

        <div
          class="flex flex-col-reverse md:flex-row justify-between my-6 text-secondary text-sm"
        >
          <p>Copyright &copy; {{ new Date().getFullYear() }}</p>
          <p @click.ctrl.alt.shift="toggleTestMode">
            Crypkit's path to flawlessness counter:
            <router-link :to="{ name: 'ReleaseNotesView' }" class="underline">{{
              releaseVersion
            }}</router-link>
          </p>
        </div>
      </div>
    </div>

    <Tour
      v-if="tourSteps.length"
      :steps="tourSteps"
      :start-step="activeTourFlow?.current_step"
      :theme="colorScheme.isDarkTheme ? 'dark' : 'light'"
      @step-changed="updateTourFlow($event, false)"
      @finished="updateTourFlow($event, true)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState } from 'pinia'

import { useColorScheme } from '@/composables/color_scheme'
import { useTestMode } from '@/composables/test_mode'
import { releaseVersion } from '@/helpers/system'
import TourScenarios, { type Step } from '@/services/tour.service'
import { useGlobalStore } from '@/stores/global'
import { useSettingsGeneralStore } from '@/stores/settings-general'
import { useUserStore } from '@/stores/user'

import Navbar from '@/components/navigation/Navbar/Index.vue'
import MobileSidebar from '@/components/navigation/MobileSidebar/Index.vue'
import Sidebar from '@/components/navigation/Sidebar/Index.vue'
import Tour from '@/components/misc/Tour'

import StartupNotifications from '@/components/misc/Notification/StartupNotifications.vue'
import VerifyEmailNotification from '@/components/misc/Notification/VerifyEmailNotification.vue'

import NoPackages from '@/views/Auth/NoPackages.vue'
import NoProduct from '@/views/Auth/NoProduct.vue'

export default defineComponent({
  name: 'AppLayout',

  components: {
    Navbar,
    MobileSidebar,
    Sidebar,
    Tour,
    StartupNotifications,
    VerifyEmailNotification,
    NoPackages,
    NoProduct,
  },

  data() {
    return {
      userStore: useUserStore(),
      globalStore: useGlobalStore(),
      settingsGeneralStore: useSettingsGeneralStore(),
      testMode: useTestMode(),
      colorScheme: useColorScheme(),
      showStartupNotifications: false,
    }
  },

  computed: {
    ...mapState(useUserStore, {
      isEmailConfirmed: (store) => store.settings.confirmed,
    }),
    ...mapState(useUserStore, [
      'noPermissionsAdmin',
      'noPackages',
      'hasProduct',
      'activeTourFlow',
      'root',
      'entity',
    ]),
    ...mapState(useGlobalStore, {
      isToggled: 'sidebarToggled',
      activeViewName: 'activeViewName',
      customHeader: 'customHeader',
    }),
    releaseVersion,
    appClass(): string {
      const { isEmailConfirmed } = this
      return isEmailConfirmed ? '' : 'with-notification'
    },
    contentClass(): string {
      const { isToggled, noPermissionsAdmin, noPackages } = this
      if (noPermissionsAdmin || noPackages) {
        return ''
      }
      if (this.$breakpoints.mdAndDown) {
        return 'with-navbar'
      }
      return isToggled ? 'with-sidebar-toggled' : 'with-sidebar'
    },
    canShowWithNoPackage(): boolean {
      return this.checkActiveRoute(['AccountSettingsView', 'NoPermissionView'])
    },
    canShowWithNoProduct(): boolean {
      return this.checkActiveRoute([
        'SettingsView',
        'AccountSettingsView',
        'NoPermissionView',
      ])
    },
    tourSteps(): Step[] {
      const { activeTourFlow } = this
      if (activeTourFlow) {
        const scenario = TourScenarios.find(
          (scenario) => scenario.key == activeTourFlow.scenario_key
        )
        return scenario?.steps || []
      }
      return []
    },
  },

  watch: {
    entity(next) {
      if (next && this.userStore.currentView === 'admin') {
        this.settingsGeneralStore.getTags()
      }
    },
  },

  created() {
    // Read once: a tour starting or ending later must not remount and rerun the queue.
    this.showStartupNotifications = !this.tourSteps.length
  },

  mounted() {
    this.getAdditionalData()
  },

  beforeUpdate() {
    const wrapper = document.getElementById('app-content-wrapper')
    if (wrapper) {
      wrapper.scrollTo(0, 0)
    }
  },

  methods: {
    async getAdditionalData() {
      try {
        await Promise.all([
          this.globalStore.getExchanges(),
          this.globalStore.getBlockchains(),
          this.settingsGeneralStore.getTags(),
        ])
      } catch (err) {
        this.$bus.emit('error', err)
      }
    },
    updateTourFlow(stepIndex: number, finished: boolean): void {
      if (this.activeTourFlow) {
        this.userStore.saveTourFlow({
          ...this.activeTourFlow,
          current_step: stepIndex,
          finished,
        })
      }
    },
    checkActiveRoute(enabledRoutes: string[]): boolean {
      return this.$route.matched.some((route) => {
        return (
          typeof route.name === 'string' &&
          enabledRoutes.indexOf(route.name) !== -1
        )
      })
    },
    toggleTestMode() {
      this.testMode.forceState(!this.testMode.enabled)
      alert(`Test mode enabled: ${this.testMode.enabled}`)
    },
  },
})
</script>
